import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import localStorageService from '../services/localStorageService';
import { ADMIN, DASHBOARD_STATCREW, DASHBOARD_STATCREW_REVIEW, TEAM_ENTITLEMENTS } from '../constants/routes';

type StoredSport = {
    code: string;
    name: string;
    badge: string;
    icon: string;
    disabled?: boolean;
};

const DEFAULT_SPORTS: StoredSport[] = [
    { code: 'MFB', name: 'Football', badge: 'M', icon: '🏈' },
    { code: 'MBB', name: 'Basketball', badge: 'M', icon: '🏀' },
    { code: 'WVB', name: 'Volleyball', badge: 'W', icon: '🏐' },
    { code: 'WSO', name: 'Soccer', badge: 'W', icon: '⚽' },
    { code: 'MIH', name: 'Ice Hockey', badge: 'M', icon: '🏒', disabled: true },
];

const SPORT_VISUALS: Record<string, Pick<StoredSport, 'name' | 'badge' | 'icon'>> = {
    MFB: { name: 'Football', badge: 'M', icon: '🏈' },
    WFB: { name: 'Football', badge: 'W', icon: '🏈' },
    MBB: { name: 'Basketball', badge: 'M', icon: '🏀' },
    WBB: { name: 'Basketball', badge: 'W', icon: '🏀' },
    WVB: { name: 'Volleyball', badge: 'W', icon: '🏐' },
    MVB: { name: 'Volleyball', badge: 'M', icon: '🏐' },
    WSO: { name: 'Soccer', badge: 'W', icon: '⚽' },
    MSO: { name: 'Soccer', badge: 'M', icon: '⚽' },
    MIH: { name: 'Ice Hockey', badge: 'M', icon: '🏒' },
    WIH: { name: 'Ice Hockey', badge: 'W', icon: '🏒' },
};

function buildSports(userData: any): StoredSport[] {
    if (!Array.isArray(userData?.sports) || userData.sports.length === 0) {
        return DEFAULT_SPORTS;
    }

    return userData.sports
        .map((sport: any) => {
            const code = typeof sport?.code === 'string' ? sport.code : '';

            if (!code) {
                return null;
            }

            const fallbackVisual = SPORT_VISUALS[code] || {
                name: sport?.name || code,
                badge: typeof sport?.gender === 'string' ? sport.gender.charAt(0).toUpperCase() : 'M',
                icon: '🏆',
            };

            return {
                code,
                name: sport?.name || fallbackVisual.name,
                badge: fallbackVisual.badge,
                icon: fallbackVisual.icon,
                disabled: false,
            };
        })
        .filter(Boolean);
}

function ArrowIcon({ direction }: { direction: 'left' | 'right' }) {
    const rotationClass = direction === 'left' ? 'rotate-180' : '';

    return (
        <svg
            className={`h-4 w-4 ${rotationClass}`}
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M7 4L13 10L7 16"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ProfileIcon() {
    return (
        <svg
            className="h-7 w-7 text-slate-900"
            viewBox="0 0 24 24"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" />
            <path d="M3 21C3 17.134 7.02944 14 12 14C16.9706 14 21 17.134 21 21V22H3V21Z" />
        </svg>
    );
}

// Visible window size: only 4 cards shown at a time
const VISIBLE_COUNT = 4;

function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const userData = localStorageService.getUserInfo<any>();
    const [entitledSports, setEntitledSports] = useState<StoredSport[] | null>(null);
    const sports = entitledSports ?? buildSports(userData);
    const [selectedSport, setSelectedSport] = useState(
        localStorageService.getSelectedSport() || sports[0]?.code || DEFAULT_SPORTS[0].code
    );
    // Index of the first visible sport card
    const [startIndex, setStartIndex] = useState(0);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const showArrows = sports.length > VISIBLE_COUNT;
    const canScrollLeft = startIndex > 0;
    const canScrollRight = startIndex + VISIBLE_COUNT < sports.length;

    useEffect(() => {
        let isActive = true;

        function authHeader(): Record<string, string> {
            const stored = localStorageService.getUserInfo<any>();
            const token = stored?.access_token || stored?.accessToken;
            return token ? { Authorization: `Bearer ${token}` } : {};
        }

        async function loadEntitlements() {
            const teamId = userData?.team?.id;
            if (!teamId) return;
            try {
                const url = new URL(TEAM_ENTITLEMENTS);
                url.searchParams.set('team_id', String(teamId));
                const res = await fetch(url.toString(), { headers: authHeader() });
                const payload = await res.json().catch(() => ({}));
                if (!isActive) return;
                if (!res.ok || !Array.isArray(payload?.sports)) return;
                const nextSports: StoredSport[] = payload.sports
                    .map((s: any) => {
                        const code = String(s?.sport_code ?? '').toUpperCase();
                        if (!code) return null;
                        const visual = SPORT_VISUALS[code] || {
                            name: code,
                            badge: code.startsWith('W') ? 'W' : 'M',
                            icon: '🏆',
                        };
                        return {
                            code,
                            name: visual.name,
                            badge: visual.badge,
                            icon: visual.icon,
                            disabled: false,
                        } as StoredSport;
                    })
                    .filter(Boolean) as StoredSport[];
                if (nextSports.length > 0) setEntitledSports(nextSports);
            } catch {
                // fall back to sports from login payload
            }
        }

        void loadEntitlements();
        return () => {
            isActive = false;
        };
    }, []);

    useEffect(() => {
        // Reconcile the stored sport ONLY against the authoritative entitlements
        // list. Before it loads, `sports` is a fallback (login payload or
        // DEFAULT_SPORTS) that may lack sports like MBS/WSB — reconciling against
        // it stomped the stored sport back to Football on every full-page load
        // (e.g. the Review button, which navigates via window.location.assign).
        if (entitledSports === null) return;
        const hasSelectedSport = entitledSports.some((sport) => sport.code === selectedSport);

        if (!hasSelectedSport && entitledSports[0]?.code) {
            const nextSport = entitledSports[0].code;
            setSelectedSport(nextSport);
            localStorageService.setSelectedSport(nextSport);
        }
    }, [selectedSport, entitledSports]);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isDropdownOpen]);

    const handleLogout = () => {
        localStorageService.clearAll();
        navigate('/login');
    };

    const handleDashboardClick = () => {
        setIsDropdownOpen(false);
        navigate('/dashboard');
    };

    const handleAdminClick = () => {
        setIsDropdownOpen(false);
        navigate(ADMIN);
    };

    const isAdmin = !!userData?.team_admin;

    const teamName =
        userData?.team?.name ||
        userData?.teamName ||
        userData?.schoolName ||
        '';
    const teamLogo =
        userData?.team?.imgUrl ||
        userData?.teamLogo ||
        '/athlyte_logo.png';
    const userName =
        userData?.fullName ||
        userData?.name ||
        userData?.username ||
        '';

    const handleSportSelect = (sportCode: string, disabled?: boolean) => {
        if (disabled) return;

        // A sport card acts as "go to this sport's StatCrew dashboard" from ANY
        // StatCrew subpage (upload wizard, review, job preview, assets) — and
        // re-clicking the currently selected sport navigates too. Leaving an
        // in-progress review asks for confirmation first (progress is saved
        // server-side per step; the job can be re-opened from the dashboard).
        const onStatcrewSubpage = location.pathname.startsWith(DASHBOARD_STATCREW);
        if (location.pathname.startsWith(DASHBOARD_STATCREW_REVIEW)) {
            const confirmed = window.confirm(
                'Leave this review and go to the StatCrew dashboard? Saved steps are kept; you can re-open the review from the dashboard.'
            );
            if (!confirmed) return;
        }

        setSelectedSport(sportCode);
        localStorageService.setSelectedSport(sportCode);

        // Pages under /dashboard/statcrew are season/job-locked via URL params
        // (?selectedSport&season, review's ?jobId). Navigation must NOT keep the
        // old season — that bypasses the per-sport season blocker — nor a jobId
        // from another sport's review. `season`/`jobId` are intentionally
        // dropped; `username` is preserved. StatCrewTable then recomputes the
        // next processable season for the selected sport.
        if (onStatcrewSubpage) {
            const params = new URLSearchParams(location.search);
            params.delete('season');
            params.delete('jobId');
            params.delete('job_id');
            params.set('selectedSport', sportCode);
            navigate(`${DASHBOARD_STATCREW}?${params.toString()}`);
        }
    };

    const scrollSports = (direction: 'left' | 'right') => {
        if (direction === 'left') {
            setStartIndex((prev) => Math.max(0, prev - 1));
        } else {
            setStartIndex((prev) => Math.min(sports.length - VISIBLE_COUNT, prev + 1));
        }
    };

    const visibleSports = sports.slice(startIndex, startIndex + VISIBLE_COUNT);

    return (
        <header className="border-b border-slate-200 bg-white shadow-[0_18px_45px_-28px_rgba(15,23,42,0.35)]">
            <div className="mx-auto flex w-full max-w-[1500px] items-center gap-3 px-4 py-3 lg:px-6">

                {/* Left: Logo + Team Name */}
                <Link to="/dashboard" className="flex min-w-fit items-center gap-2 pr-2 no-underline">
                    <img
                        src={teamLogo}
                        alt={teamName}
                        className="h-9 w-9 rounded-full border border-slate-200 bg-white object-contain p-0.5"
                    />
                    <div>
                        <p className="text-base font-semibold text-slate-900">{teamName}</p>
                    </div>
                </Link>

                {/* Center: Sport Cards */}
                <div className="flex flex-1 items-center justify-center gap-2">
                    {/* Left arrow — only when more than 4 sports */}
                    {showArrows && (
                        <button
                            type="button"
                            onClick={() => scrollSports('left')}
                            disabled={!canScrollLeft}
                            className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-full transition md:inline-flex ${
                                canScrollLeft
                                    ? 'bg-cyan-500 text-white shadow-[0_8px_20px_-12px_rgba(6,182,212,1)] hover:bg-cyan-600'
                                    : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                            }`}
                            aria-label="Scroll sports left"
                        >
                            <ArrowIcon direction="left" />
                        </button>
                    )}

                    {/* Exactly 4 visible cards */}
                    <div className="flex items-center gap-2">
                        {visibleSports.map((sport) => {
                            const isActive = selectedSport === sport.code;

                            return (
                                <button
                                    key={sport.code}
                                    type="button"
                                    onClick={() => handleSportSelect(sport.code, sport.disabled)}
                                    disabled={sport.disabled}
                                    className={`relative flex min-h-[70px] w-[120px] shrink-0 flex-col items-center justify-center rounded-2xl border px-3 py-1 text-center transition ${
                                        sport.disabled
                                            ? 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
                                            : isActive
                                              ? 'border-sky-400 bg-cyan-50 shadow-[0_8px_24px_-14px_rgba(14,165,233,0.9)]'
                                              : 'border-slate-200 bg-white text-slate-700 shadow-sm hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-md'
                                    }`}
                                >
                                    {isActive && (
                                        <span className="absolute left-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-cyan-500 text-[9px] font-bold text-white">
                                            ✓
                                        </span>
                                    )}
                                    <span
                                        className={`absolute right-2 top-2 rounded-md px-1.5 py-0.5 text-[8px] font-bold ${
                                            sport.badge === 'W'
                                                ? 'bg-pink-100 text-pink-500'
                                                : 'bg-sky-100 text-sky-600'
                                        }`}
                                    >
                                        {sport.badge}
                                    </span>
                                    <span className="text-[28px] leading-none">{sport.icon}</span>
                                    <span
                                        className={`mt-2 text-xs font-semibold ${
                                            isActive ? 'text-cyan-600' : 'text-slate-800'
                                        }`}
                                    >
                                        {sport.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Right arrow — only when more than 4 sports */}
                    {showArrows && (
                        <button
                            type="button"
                            onClick={() => scrollSports('right')}
                            disabled={!canScrollRight}
                            className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-full transition md:inline-flex ${
                                canScrollRight
                                    ? 'bg-cyan-500 text-white shadow-[0_8px_20px_-12px_rgba(6,182,212,1)] hover:bg-cyan-600'
                                    : 'bg-slate-100 text-slate-300 cursor-not-allowed'
                            }`}
                            aria-label="Scroll sports right"
                        >
                            <ArrowIcon direction="right" />
                        </button>
                    )}
                </div>

                {/* Right: Profile */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        type="button"
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="ml-2 flex min-w-fit items-center gap-2 rounded-full border border-transparent bg-transparent px-2 py-1.5 text-left transition hover:border-slate-200 hover:bg-slate-50"
                    >
                        <ProfileIcon />
                        <span className="hidden text-sm font-semibold text-slate-800 xl:inline">{userName}</span>
                        <svg
                            className={`hidden h-4 w-4 text-slate-600 xl:block transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            aria-hidden="true"
                        >
                            <path
                                d="M5 8L10 13L15 8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {isDropdownOpen && (
                        <div className="absolute right-0 top-full z-50 mt-1 w-44 rounded-lg border border-[#DEE7F4] bg-white py-2 shadow-lg">
                            {/* Username Display */}
                            <div className="border-b border-[#DEE7F4] px-4 py-3 text-center">
                                <p className="text-sm font-semibold text-slate-900">{userName}</p>
                            </div>
                            
                            {/* Dashboard Link */}
                            <button
                                onClick={handleDashboardClick}
                                className="block w-full px-4 py-2 text-left text-sm text-[#292929] hover:bg-gray-100 text-center"
                            >
                                Dashboard
                            </button>

                            {isAdmin && (
                                <button
                                    onClick={handleAdminClick}
                                    className="block w-full px-4 py-2 text-left text-sm text-[#292929] hover:bg-gray-100 text-center"
                                >
                                    Admin
                                </button>
                            )}

                            {/* Sign Out */}
                            <button
                                onClick={handleLogout}
                                className="block w-full px-4 py-2 text-left text-sm font-medium text-[#C22222] hover:bg-gray-100 text-center"
                            >
                                Sign out
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
}

export default Header;